import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCouponAuth } from '../hooks/use-coupon-auth';
import { useClipCoupon } from '../hooks/use-clip-coupon';
import { useUserLocation } from '@/Locations/hooks/use-user-location';
import { useLocations } from '@/Locations/hooks/use-locations';
import type { Location } from '@/Locations/types/locations';
import type { Coupon } from '../types/coupons';
import { SignInForm } from './SignInForm';
import { CreateAccountSheet } from './CreateAccountSheet';
import { IconLoader4, IconMapPin, IconSearch, IconShieldCheck, IconTag } from '@tabler/icons-react';

interface CouponAuthModalProps {
  coupon: Coupon | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which form to show after the store gate. Defaults to 'signin'. */
  initialMode?: 'signin' | 'register';
}

export const CouponAuthModal = ({ coupon, open, onOpenChange, initialMode = 'signin' }: CouponAuthModalProps) => {
  const [step, setStep] = useState<'location' | 'signin'>('signin');
  const [mode, setMode] = useState<'signin' | 'register'>(initialMode);
  const skipReseedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');

  const { isAuthenticated } = useCouponAuth();
  const clipCouponMutation = useClipCoupon();

  // Location hooks
  const { branchId, setLocation: saveLocation, selectedLocation } = useUserLocation();
  const { data: locations, isLoading: isLoadingLocations } = useLocations();

  // Only locations with coupons enabled and a valid LocationCouponID
  const couponLocations = useMemo(() => {
    if (!locations) return [];
    return locations.filter(loc => loc.LocationCoupons && loc.LocationCouponID);
  }, [locations]);

  // Filter by search query and sort alphabetically
  const filteredLocations = useMemo(() => {
    let filtered = couponLocations;
    if (locationSearchQuery.trim()) {
      const query = locationSearchQuery.toLowerCase();
      filtered = couponLocations.filter(loc => {
        const searchString = [
          loc.LocationTitle,
          loc.LocationAddress?.LocationAddressLineOne,
          loc.LocationAddress?.LocationAddressCity,
          loc.LocationAddress?.LocationAddressState,
          loc.LocationAddress?.LocationAddressZipCode,
        ].filter(Boolean).join(' ').toLowerCase();
        return searchString.includes(query);
      });
    }
    return [...filtered].sort((a, b) => a.LocationTitle.localeCompare(b.LocationTitle));
  }, [couponLocations, locationSearchQuery]);

  const needsLocationSelection = !branchId;

  // Set initial view when modal opens
  useEffect(() => {
    if (open && !isAuthenticated) {
      if (skipReseedRef.current) {
        // Reopened internally (e.g., the sheet's "Sign In" link) — keep the
        // mode/step that handler already set instead of re-seeding from the prop.
        skipReseedRef.current = false;
        return;
      }
      setMode(initialMode);
      if (needsLocationSelection) {
        setStep('location');
      } else if (initialMode === 'register') {
        // Store already selected: go straight to the create-account form.
        handleOpenChange(false);
        setCreateAccountOpen(true);
      } else {
        setStep('signin');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsLocationSelection, isAuthenticated, initialMode]);

  const handleLocationSelect = (location: Location) => {
    saveLocation(location);
    setLocationSearchQuery('');
    if (mode === 'register') {
      handleOpenChange(false);
      setCreateAccountOpen(true);
    } else {
      setStep('signin');
    }
  };

  // Auto-clip when already authenticated
  useEffect(() => {
    if (open && isAuthenticated && coupon && !clipCouponMutation.isPending && !clipCouponMutation.isSuccess) {
      const offerId = coupon.id;
      if (offerId) {
        clipCouponMutation.mutate(offerId, {
          onSuccess: () => {
            setTimeout(() => {
              onOpenChange(false);
              resetForm();
            }, 1000);
          },
          onError: (err) => {
            setError(err instanceof Error ? err.message : 'Failed to clip coupon. Please try again.');
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated, coupon?.id]);

  const handleSignInSuccess = () => {
    // After sign-in, clip the coupon if one was provided
    if (coupon?.id) {
      clipCouponMutation.mutate(coupon.id, {
        onSuccess: () => {
          setTimeout(() => {
            onOpenChange(false);
            resetForm();
          }, 1000);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to clip coupon. Please try again.');
        },
      });
    } else {
      onOpenChange(false);
      resetForm();
    }
  };

  const handleCreateAccountSuccess = () => {
    // After account creation (which auto-signs in), clip coupon if provided
    if (coupon?.id) {
      clipCouponMutation.mutate(coupon.id, {
        onSuccess: () => {
          setTimeout(() => {
            onOpenChange(false);
            resetForm();
          }, 1000);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to clip coupon. Please try again.');
        },
      });
    } else {
      onOpenChange(false);
      resetForm();
    }
  };

  const resetForm = () => {
    setStep(needsLocationSelection ? 'location' : 'signin');
    setError(null);
    setLocationSearchQuery('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetForm();
    onOpenChange(newOpen);
  };

  // If already authenticated, show loading state while clipping
  if (isAuthenticated && coupon && step === 'signin' && clipCouponMutation.isPending) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconShieldCheck className="h-5 w-5" />
              Clipping Coupon
            </DialogTitle>
            <DialogDescription>
              Adding this coupon to your account...
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-8">
            <IconLoader4 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className={step === 'location' ? 'sm:max-w-lg' : 'sm:max-w-md'}>
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2">
              {step === 'location' ? <IconMapPin className="h-5 w-5" /> : <IconShieldCheck className="h-5 w-5" />}
              {step === 'location' ? 'Select Your Store' : 'Sign In to Clip Coupons'}
            </DialogTitle>
            <DialogDescription>
              {step === 'location'
                ? 'Choose your store to access digital coupons and savings.'
                : 'Sign in with your email and password to clip coupons.'}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 'location' ? (
            <div className="space-y-4">
              <div className="relative">
                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by city, state, or zip code"
                  value={locationSearchQuery}
                  onChange={(e) => setLocationSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <ScrollArea className="h-[300px]">
                {isLoadingLocations ? (
                  <div className="flex justify-center py-8">
                    <IconLoader4 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : filteredLocations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {locationSearchQuery
                      ? 'No stores found matching your search.'
                      : 'No stores with coupons available.'}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredLocations.map((location) => {
                      const address = [
                        location.LocationAddress?.LocationAddressLineOne,
                        location.LocationAddress?.LocationAddressCity,
                        location.LocationAddress?.LocationAddressState,
                        location.LocationAddress?.LocationAddressZipCode,
                      ].filter(Boolean).join(', ');

                      return (
                        <Card
                          key={location.LocationCouponID}
                          className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleLocationSelect(location)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                              <IconMapPin className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-foreground truncate">
                                  {location.LocationTitle}
                                </h4>
                                <Badge variant="secondary" className="text-xs flex-shrink-0">
                                  <IconTag className="h-3 w-3 mr-1" />
                                  Coupons
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {address}
                              </p>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Show selected store */}
              {selectedLocation && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                  <IconMapPin className="h-4 w-4 shrink-0 text-primary" />
                  <span className="text-sm text-muted-foreground">Shopping at</span>
                  <span className="text-sm font-medium">{selectedLocation}</span>
                </div>
              )}

              <SignInForm
                onSuccess={handleSignInSuccess}
                onCreateAccount={() => {
                  handleOpenChange(false);
                  setCreateAccountOpen(true);
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <CreateAccountSheet
        open={createAccountOpen}
        onOpenChange={setCreateAccountOpen}
        onSuccess={handleCreateAccountSuccess}
        onSignIn={() => {
          setCreateAccountOpen(false);
          setMode('signin');
          setStep('signin');
          skipReseedRef.current = true;
          onOpenChange(true);
        }}
      />
    </>
  );
};
